import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

/**
 * A select you can type into.
 *
 * Dhaka has 55 thanas and Chattogram 30, so scrolling a plain <select> is the
 * real friction in choosing an address — not missing data. Typing "kot" should
 * be enough.
 *
 * Search runs over BOTH names, so "kotwali" and "কোতোয়ালী" both find the same
 * row whichever language the member is reading.
 *
 * The list is rendered in a portal, positioned over the trigger. It used to sit
 * inside the component, absolutely positioned — which the dialog's own
 * `overflow-y-auto` clipped, so the last thana in Dhaka was unreachable. Nothing
 * an ancestor does to overflow can crop it now, and it flips above the trigger
 * when the space below is too small.
 *
 * Keyboard: arrows move, Enter picks, Escape closes the list. Escape is stopped
 * here so it closes the list rather than the dialog this usually sits inside.
 */
const SearchableSelect = ({
  value,
  onChange,
  options,            // [{ id, en, bn }]
  label,
  placeholder,
  disabled = false,
  triggerClassName = '',
  id,
}) => {
  const { t, i18n } = useTranslation()
  const bn = i18n.language === 'bn'
  const text = (o) => (bn ? o.bn : o.en)

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const [box, setBox] = useState(null)

  const rootRef = useRef(null)
  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const searchRef = useRef(null)
  const listRef = useRef(null)

  const selected = options.find((o) => o.id === value) || null

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    // Match either script, so the search works regardless of keyboard.
    return options.filter(
      (o) => o.en.toLowerCase().includes(q) || (o.bn || '').includes(query.trim())
    )
  }, [options, query])

  // Position the menu over the trigger, and keep it there while anything
  // scrolls. The listener is capturing because the dialog body scrolls, not the
  // window, and scroll events on an inner element do not bubble.
  useLayoutEffect(() => {
    if (!open) { setBox(null); return }
    const place = () => {
      const r = triggerRef.current?.getBoundingClientRect()
      if (!r) return
      const GAP = 4, EDGE = 8
      const below = window.innerHeight - r.bottom - GAP - EDGE
      const above = r.top - GAP - EDGE
      const up = below < 220 && above > below
      const room = up ? above : below
      setBox({
        left: r.left,
        width: r.width,
        top: up ? undefined : r.bottom + GAP,
        bottom: up ? window.innerHeight - r.top + GAP : undefined,
        // Minus the search field, so the whole menu stays within `room`.
        listMax: Math.max(120, Math.min(288, room - 60)),
      })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open])

  // The menu lives outside this component's DOM subtree, so it has to be named
  // explicitly here — testing `rootRef` alone would close the list on mousedown
  // and the option's click would then land on nothing.
  useEffect(() => {
    if (!open) return
    const onDown = (e) => {
      if (rootRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  useEffect(() => {
    if (open) {
      setQuery('')
      setActive(Math.max(0, filtered.findIndex((o) => o.id === value)))
      // Deliberately not autofocused on touch devices: raising the keyboard
      // immediately hides most of the list the member came to look at.
      const touch = window.matchMedia?.('(pointer: coarse)').matches
      if (!touch) searchRef.current?.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    if (!open || !listRef.current) return
    const el = listRef.current.children[active]
    el?.scrollIntoView({ block: 'nearest' })
  }, [active, open])

  const pick = (option) => {
    onChange(option.id)
    setOpen(false)
    triggerRef.current?.focus()
  }

  // Keydown is handled here, on the root. Events raised inside the portal still
  // reach it: React propagates them along the component tree, not the DOM tree.
  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (open) { e.preventDefault(); e.stopPropagation(); setOpen(false) }
      return
    }
    if (!open && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
      e.preventDefault(); setOpen(true); return
    }
    if (!open) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, filtered.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[active]) pick(filtered[active]) }
  }

  return (
    <div className="relative" ref={rootRef} onKeyDown={onKeyDown}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium mb-1">{label}</label>
      )}

      <button
        id={id}
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        // min-h-[44px] is the smallest comfortable touch target on a phone.
        className={`w-full min-h-[44px] flex items-center justify-between gap-2 text-left
                    disabled:opacity-60 disabled:cursor-not-allowed ${triggerClassName}`}
      >
        <span className={`truncate ${selected ? '' : 'opacity-60'}`}>
          {selected ? text(selected) : placeholder}
        </span>
        <span aria-hidden="true" className="shrink-0 opacity-60">▾</span>
      </button>

      {open && box && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', left: box.left, width: box.width, top: box.top, bottom: box.bottom }}
          className="z-[300] rounded-lg border border-border bg-card shadow-strong overflow-hidden"
        >
          <div className="p-2 border-b border-border">
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActive(0) }}
              placeholder={t('address.search')}
              aria-label={t('address.search')}
              className="w-full min-h-[40px] rounded-md border border-border bg-card text-foreground
                         px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <ul
            ref={listRef}
            role="listbox"
            style={{ maxHeight: box.listMax }}
            className="overflow-y-auto overscroll-contain py-1"
          >
            {filtered.length === 0 && (
              <li className="px-3 py-3 text-sm text-muted-foreground">{t('address.noMatch')}</li>
            )}
            {filtered.map((o, i) => (
              <li key={o.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={o.id === value}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(o)}
                  className={`w-full text-left px-3 py-2.5 text-sm min-h-[44px]
                              ${i === active ? 'bg-muted' : ''}
                              ${o.id === value ? 'text-primary font-medium' : 'text-foreground'}`}
                >
                  {text(o)}
                  {/* The other script, so a member can confirm they picked right. */}
                  <span className="block text-xs text-muted-foreground truncate">
                    {bn ? o.en : o.bn}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>,
        document.body
      )}
    </div>
  )
}

export default SearchableSelect
